const HTML_ENTITIES = {
  '&quot;': '"',
  '&#34;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&nbsp;': ' ',
  '&#039;': "'",
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&rdquo;': '”',
  '&ldquo;': '“',
  '&ndash;': '–',
  '&mdash;': '—',
};

// Scraped titles/snippets sometimes arrive HTML-escaped (e.g. "&quot;") instead of
// decoded, so unescape the common entities before any other cleanup runs.
function decodeHtmlEntities(text) {
  return text.replace(/&(?:quot|#34|#039|apos|#39|amp|lt|gt|nbsp|rsquo|lsquo|rdquo|ldquo|ndash|mdash);/g, (match) => HTML_ENTITIES[match] || match);
}

// Strips common markdown syntax so record snippets render as plain text.
// Scraped snippets often carry heading markers mid-sentence rather than at a true
// line start, so headers/bullets are stripped wherever they appear, not just ^-anchored.
export function stripMarkdown(text) {
  if (!text) return text;
  return decodeHtmlEntities(text)
    .replace(/(^|\s)#{1,6}\s+/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/(^|\s)[-*]\s+/g, '$1')
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Formats a date as relative time ("6 days ago"); falls back to the date itself once it's old.
export function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 0) return 'just now';
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

export function absoluteDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString();
}

export function formatShortDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function recordKey(record) {
  return record?.id || record?.url || '';
}

export function isWatchlisted(record, watchlist) {
  const key = recordKey(record);
  return key ? watchlist.includes(key) : false;
}

const IST_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function istDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    IST_DATE_FORMATTER.formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function addDaysToKey(dateKey, offsetDays) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const utcNoon = new Date(Date.UTC(year, month - 1, day + offsetDays, 12, 0, 0));
  return istDateKey(utcNoon);
}

function dateKeyToIstRange(dateKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey || '')) return null;
  return {
    from_date: `${dateKey}T00:00:00+05:30`,
    to_date: `${dateKey}T23:59:59.999+05:30`,
  };
}

export function todayIstKey() {
  return istDateKey(new Date());
}

export function getDateInputMax() {
  return todayIstKey() || new Date().toISOString().slice(0, 10);
}

export function dateFilterToApiParams(filter) {
  if (!filter || filter.preset === 'all') return {};
  const todayKey = todayIstKey();
  if (!todayKey) return {};

  if (filter.preset === 'today') return dateKeyToIstRange(todayKey) || {};
  if (filter.preset === 'yesterday') return dateKeyToIstRange(addDaysToKey(todayKey, -1)) || {};
  if (filter.preset === 'custom' && filter.customDate) {
    return dateKeyToIstRange(filter.customDate) || {};
  }
  if (filter.preset === '7d') {
    return {
      from_date: `${addDaysToKey(todayKey, -6)}T00:00:00+05:30`,
      to_date: `${todayKey}T23:59:59.999+05:30`,
    };
  }
  if (filter.preset === '30d') {
    return {
      from_date: `${addDaysToKey(todayKey, -29)}T00:00:00+05:30`,
      to_date: `${todayKey}T23:59:59.999+05:30`,
    };
  }
  return {};
}

/** @typedef {{ preset: string, customDate?: string }} DateFilter */

export function getDateFilterLabel(filter) {
  if (!filter || filter.preset === 'all') return 'All time';
  if (filter.preset === 'today') return 'Today';
  if (filter.preset === 'yesterday') return 'Yesterday';
  if (filter.preset === '7d') return 'Last 7 days';
  if (filter.preset === '30d') return 'Last 30 days';
  if (filter.preset === 'custom' && filter.customDate) {
    return new Date(`${filter.customDate}T12:00:00`).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  }
  return 'All time';
}

export function matchesDateFilter(publishedAt, filter) {
  if (!filter || filter.preset === 'all') return true;
  if (!publishedAt) return false;
  const itemKey = istDateKey(publishedAt);
  const todayKey = istDateKey(new Date());
  if (!itemKey || !todayKey) return false;

  if (filter.preset === 'today') return itemKey === todayKey;
  if (filter.preset === 'yesterday') return itemKey === addDaysToKey(todayKey, -1);
  if (filter.preset === '7d') {
    const start = addDaysToKey(todayKey, -6);
    return itemKey >= start && itemKey <= todayKey;
  }
  if (filter.preset === '30d') {
    const start = addDaysToKey(todayKey, -29);
    return itemKey >= start && itemKey <= todayKey;
  }
  if (filter.preset === 'custom' && filter.customDate) {
    return itemKey === filter.customDate;
  }
  return true;
}
